import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { DollarSign, Plus, Trash2, X, Edit2, Check } from "lucide-react";

export default function ServicePackages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", sell_price: "", contractor_payout: "", delivery_days: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/service-packages");
      setRows(data.packages || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      name: form.name,
      description: form.description,
      sell_price: parseFloat(form.sell_price),
      contractor_payout: parseFloat(form.contractor_payout),
      delivery_days: parseInt(form.delivery_days, 10),
    };
    try {
      if (editing) await api.patch(`/service-packages/${editing}`, body);
      else await api.post("/service-packages", body);
      toast.success(editing ? "Package updated." : "Package added.");
      setCreating(false);
      setEditing(null);
      setForm({ name: "", description: "", sell_price: "", contractor_payout: "", delivery_days: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`/service-packages/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (pkg) => {
    setEditing(pkg.id);
    setForm({
      name: pkg.name,
      description: pkg.description,
      sell_price: String(pkg.sell_price),
      contractor_payout: String(pkg.contractor_payout),
      delivery_days: String(pkg.delivery_days),
    });
    setCreating(true);
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="service-packages-page">
      <Toaster position="top-right" richColors />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><DollarSign className="w-3.5 h-3.5" /> Service packages</div>
          <h1 className="text-3xl font-bold text-neutral-900">Pricing & delivery catalog</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl text-sm">
            Every service you sell. Each package captures the client sell price, contractor payout, and expected delivery window.
            Margin = sell − payout.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setCreating(true); }} className="lf-btn-dark text-sm inline-flex items-center gap-2" data-testid="packages-add-btn">
          <Plus className="w-4 h-4" /> New package
        </button>
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div> : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((p) => {
            const margin = p.sell_price - p.contractor_payout;
            const marginPct = p.sell_price > 0 ? Math.round((margin / p.sell_price) * 100) : 0;
            return (
              <div key={p.id} className="bg-white border border-neutral-200 p-5" data-testid={`package-${p.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="font-display text-xl font-bold">{p.name}</div>
                    <div className="text-xs text-neutral-400 font-mono">{p.id}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(p)} className="p-1.5 text-neutral-500 hover:text-neutral-900" title="Edit"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(p.id)} className="p-1.5 text-neutral-400 hover:text-rose-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="text-sm text-neutral-600 mb-4">{p.description}</p>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div><div className="overline">Sell</div><strong>${p.sell_price.toLocaleString()}</strong></div>
                  <div><div className="overline">Payout</div><strong>${p.contractor_payout.toLocaleString()}</strong></div>
                  <div><div className="overline">Margin</div><strong className="text-emerald-700">${margin.toLocaleString()}<span className="text-xs opacity-70"> ({marginPct}%)</span></strong></div>
                  <div><div className="overline">Days</div><strong>{p.delivery_days}</strong></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <div onClick={() => { setCreating(false); setEditing(null); }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-md border border-neutral-900 shadow-2xl">
            <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between">
              <div><div className="text-xs font-mono uppercase tracking-widest text-amber-300">{editing ? "Edit" : "New"} package</div><div className="text-xl font-bold mt-1">Pricing & delivery</div></div>
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid gap-3 text-sm">
              <label className="grid gap-1"><span className="overline">Name · required</span><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid="package-form-name" /></label>
              <label className="grid gap-1"><span className="overline">Description</span><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-neutral-300 px-3 py-2" /></label>
              <div className="grid grid-cols-3 gap-2">
                <label className="grid gap-1"><span className="overline">Sell $</span><input required type="number" min="1" step="0.01" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid="package-form-sell" /></label>
                <label className="grid gap-1"><span className="overline">Payout $</span><input required type="number" min="0" step="0.01" value={form.contractor_payout} onChange={(e) => setForm({ ...form, contractor_payout: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid="package-form-payout" /></label>
                <label className="grid gap-1"><span className="overline">Days</span><input required type="number" min="1" max="365" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid="package-form-days" /></label>
              </div>
              <button type="submit" className="bg-neutral-900 text-white px-4 py-2.5 font-semibold hover:bg-neutral-700" data-testid="package-form-submit">
                <Check className="w-4 h-4 inline mr-1" /> {editing ? "Save changes" : "Add package"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
